import React, { useState, useEffect, useMemo } from 'react'
import * as Form from '@radix-ui/react-form'
import { CountryCode, parsePhoneNumber, getCountryCallingCode } from 'libphonenumber-js'
import styled, { css } from 'styled-components'
import { type FieldError } from 'react-hook-form'

// ** Styled Components
const PhoneInputContainer = styled.div<{ $fullWidth?: boolean }>`
    position: relative;
    display: inline-block;
    ${({ $fullWidth }) => $fullWidth && `width: 100%;`}
`

interface InputFieldProps {
	name: string
	error?: FieldError | undefined
	$isError?: boolean
	$fullWidth?: boolean
	disabled?: boolean
	$isSuccess?: boolean
	$hasCountrySelect?: boolean
}

const InputFieldStyles = css<InputFieldProps>`
    cursor: text;
    appearance: none;
    border-width: 0;
    outline: none;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
        transition: background-color 5000s ease-in-out 0s;
    }

    min-height: 2.875rem;
    border: 1px solid ${({ theme }) => theme.pallette.text.black50};
    padding: 0.75rem;
    ${({ $hasCountrySelect }) => $hasCountrySelect && `padding-left: 3.5rem;`}
    font-size: 1rem;
    font-weight: 400;
    font-family: inherit;
    line-height: 1;
    letter-spacing: normal;
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    box-shadow: 0px 1px 3px 0px rgba(132, 136, 152, 0.15);
    border-radius: 0.5rem;
    background-color: ${({ theme }) => theme.pallette.geniiWhite};
    ${({ $fullWidth }) => $fullWidth && `width: 100%;`}

    &:disabled {
        cursor: default;
        opacity: 0.5;
    }

    &:focus,
    &:not(:placeholder-shown),
    &:-webkit-autofill {
        border-color: ${({ theme }) => theme.pallette.text.black75};

        ~ label {
            top: -0.3rem;
            left: 0.75rem;
            color: ${({ theme }) => theme.pallette.text.black75};
            padding: 0 0.25rem;
            font-size: 0.625rem;
        }
    }

    ${({ theme, $isSuccess }) =>
		$isSuccess &&
		`
        border-color: ${theme.pallette.success.main} !important;
        ~ label {
            color: ${theme.pallette.success.main} !important;
        }
    `}

    ${({ name, error, theme }) =>
		error !== undefined &&
		`
        &[name="${name}"] {
            border-color: ${theme.pallette.error.main} !important;
            ~ label {
                color: ${theme.pallette.error.main} !important;
            }
        }
    `}
`

const PhoneInputStyled = styled.input<InputFieldProps>`
    ${InputFieldStyles}
`

const FormLabelStyled = styled(Form.FormLabel) <{ $isSuccess?: boolean }>`
    top: 1rem;
    left: 3.5rem;
    color: ${({ theme, $isSuccess }) =>
		$isSuccess ? theme.pallette.success.main : theme.pallette.text.black50};
    position: absolute;
    font-size: 0.875rem;
    font-weight: 400;
    background-color: ${({ theme }) => theme.pallette.geniiWhite};
    transition: 0.2s ease all;
    line-height: 1;
    width: fit-content;
    max-width: 60%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    pointer-events: none;
`

const CountrySelectContainer = styled.div`
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    gap: 0.25rem;
    z-index: 2;
`

const CountrySelect = styled.select`
    appearance: none;
    background: transparent;
    border: none;
    font-size: 0.875rem;
    cursor: pointer;
    outline: none;
    width: 3rem;
    height: 1.5rem;
    color: transparent;
   
    &:focus {
        outline: none;
    }
   
    option {
        background-color: ${({ theme }) => theme.pallette.geniiWhite};
        color: ${({ theme }) => theme.pallette.text.black75};
        padding: 0.25rem;
        font-size: 0.875rem;
    }
`

const SelectedCountryDisplay = styled.div`
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    font-size: 1rem;
    font-weight: 400;
    font-family: inherit;
    line-height: 1;
    letter-spacing: normal;
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    pointer-events: none;
    color: ${({ theme }) => theme.pallette.text.black75};
`



interface PhoneInputProps {
	value?: string
	onChange: (value: string | undefined) => void
	error?: FieldError
	label?: string
	name?: string
	fullWidth?: boolean
	disabled?: boolean
	placeholder?: string
	defaultCountry?: CountryCode
	startIcon?: React.ReactNode
	maxLength?: number
}

const AVAILABLE_COUNTRIES: CountryCode[] = ['US', 'CA']

export const PhoneInput: React.FC<PhoneInputProps> = ({
	value,
	onChange,
	error,
	label = 'Phone number',
	name = 'phoneNumber',
	fullWidth = false,
	disabled = false,
	placeholder = '',
	defaultCountry = 'US',
	startIcon,
	maxLength = 17
}) => {
	const [selectedCountry, setSelectedCountry] = useState<CountryCode>(defaultCountry)

	// Only sync selectedCountry when an externally-set valid number arrives (e.g. invitation pre-fill).
	useEffect(() => {
		if (!value) return
		try {
			const parsed = parsePhoneNumber(value)
			if (parsed?.isValid() && parsed.country) {
				setSelectedCountry(parsed.country as CountryCode)
			}
		} catch { }
	}, [value])

	// Compute the national-number digits to display directly from the form value.
	// Single source of truth — eliminates the local inputValue state and the
	const displayValue = useMemo(() => {
		if (!value) return ''
		try {
			const parsed = parsePhoneNumber(value)
			if (parsed?.isValid()) return parsed.nationalNumber
		} catch { }
		const callingCode = getCountryCallingCode(selectedCountry)
		return value.replace(new RegExp(`^\\+${callingCode}`), '').replace(/\D/g, '')
	}, [value, selectedCountry])

	const isSuccess = useMemo(() => {
		if (!value) return false
		try {
			return parsePhoneNumber(value)?.isValid() ?? false
		} catch {
			return false
		}
	}, [value])

	const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const newCountry = e.target.value as CountryCode
		setSelectedCountry(newCountry)

		if (displayValue) {
			const callingCode = getCountryCallingCode(newCountry)
			onChange(`+${callingCode}${displayValue}`)
		}
	}

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		let rawValue = e.target.value

		// Remove any non-digit characters
		rawValue = rawValue.replace(/\D/g, '')

		// Limit to 10 digits maximum for US/Canada phone numbers
		if (rawValue.length > 10) {
			rawValue = rawValue.slice(0, 10)
		}

		if (rawValue) {
			const callingCode = getCountryCallingCode(selectedCountry)
			const fullNumber = `+${callingCode}${rawValue}`

			try {
				const parsed = parsePhoneNumber(fullNumber)
				if (parsed && parsed.isValid() && parsed.country) {
					onChange(parsed.format('E.164'))
				} else {
					onChange(fullNumber)
				}
			} catch {
				onChange(fullNumber)
			}
		} else {
			// Using empty string, not undefined — RHF does not reliably propagate
			// undefined for string fields, which caused the value to revert to the
			// last non-empty stored value and snap the input back.
			onChange('')
		}
	}

	// Only show US and Canada
	const sortedCountries = AVAILABLE_COUNTRIES

	const getCountryFlag = (countryCode: CountryCode): string => {
		// Convert country code to flag emoji
		return countryCode.replace(/./g, char =>
			String.fromCodePoint(127397 + char.charCodeAt(0))
		)
	}

	const getCountryName = (countryCode: CountryCode): string => {
		const countryNames: Record<string, string> = {
			'US': 'United States',
			'CA': 'Canada'
		}
		return countryNames[countryCode] || countryCode
	}

	return (
		<PhoneInputContainer $fullWidth={fullWidth}>
			<Form.Control asChild>
				<PhoneInputStyled
					name={name}
					type="tel"
					value={displayValue}
					placeholder={placeholder}
					disabled={disabled}
					onChange={handleInputChange}
					error={error}
					$fullWidth={fullWidth}
					$hasCountrySelect={true}
					$isSuccess={isSuccess}
					maxLength={maxLength}
				/>
			</Form.Control>

			<CountrySelectContainer>
				<CountrySelect
					value={selectedCountry}
					onChange={handleCountryChange}
					disabled={disabled}
					title={`${getCountryName(selectedCountry)} +${getCountryCallingCode(selectedCountry)}`}
				>
					{sortedCountries.map((country) => (
						<option key={country} value={country}>
							{getCountryFlag(country)} {getCountryName(country)} +{getCountryCallingCode(country)}
						</option>
					))}
				</CountrySelect>
				<SelectedCountryDisplay>
					{getCountryFlag(selectedCountry)} +{getCountryCallingCode(selectedCountry)}
				</SelectedCountryDisplay>
			</CountrySelectContainer>

			{label && (
				<FormLabelStyled $isSuccess={isSuccess}>
					{label}
				</FormLabelStyled>
			)}


		</PhoneInputContainer>
	)
}

